// Cycle controls: replace native <select> dropdowns with a click-to-cycle
// control (‹ value ›). The native select stays in the DOM (hidden) as the
// source of truth, so existing `.value`, options and `change` listeners keep
// working; the cycle control just drives it.
//
// The wrapper takes its classes from the "cycle" entry in the /design
// component catalogue (`ui/daisy-catalog.ts`), so colour/size/style picks
// apply here exactly like they do to `[data-ds]` markup.

import { currentComponentStyle } from "./daisy";

const enhanced = new WeakSet<HTMLSelectElement>();

function selectedLabel(select: HTMLSelectElement): string {
  const opt = select.selectedOptions[0] ?? select.options[select.selectedIndex];
  return (opt?.textContent ?? opt?.value ?? "—").trim() || "—";
}

function firstEnabled(select: HTMLSelectElement): number {
  for (let i = 0; i < select.options.length; i++) {
    if (!select.options[i].disabled) return i;
  }
  return -1;
}

function lastEnabled(select: HTMLSelectElement): number {
  for (let i = select.options.length - 1; i >= 0; i--) {
    if (!select.options[i].disabled) return i;
  }
  return -1;
}

/** Wrap one native select in a cycle control. Idempotent. */
export function enhanceSelect(select: HTMLSelectElement): void {
  // Opt-out for long lists (font pickers) and for selects we style ourselves.
  if (select.dataset.cycle === "off") return;
  if (enhanced.has(select)) return;
  enhanced.add(select);

  select.classList.add("cycle-native");
  select.tabIndex = -1;
  select.setAttribute("aria-hidden", "true");

  const wrap = document.createElement("div");
  const spec = currentComponentStyle("cycle");
  wrap.className = spec?.cls ?? "cycle-select";
  // Opt into the design system so a theme change re-applies the choice to
  // controls that already exist (the /design page edits the document live).
  wrap.dataset.ds = "cycle";
  for (const [key, value] of Object.entries(spec?.vars ?? {})) {
    wrap.style.setProperty(key, value);
  }
  wrap.setAttribute("role", "group");
  wrap.setAttribute(
    "aria-label",
    select.getAttribute("aria-label") || select.title || select.id || "Option",
  );

  const prev = document.createElement("button");
  prev.type = "button";
  prev.className = "cycle-step";
  prev.textContent = "‹";
  prev.title = "Previous";
  prev.setAttribute("aria-label", "Previous");

  const value = document.createElement("button");
  value.type = "button";
  value.className = "cycle-value";
  value.title = "Click to cycle · Shift-click for previous";

  const next = document.createElement("button");
  next.type = "button";
  next.className = "cycle-step";
  next.textContent = "›";
  next.title = "Next";
  next.setAttribute("aria-label", "Next");

  wrap.append(prev, value, next);

  const sync = () => {
    const label = selectedLabel(select);
    value.textContent = label;
    value.title = label;
    const disabled = select.disabled || select.options.length === 0;
    // Attribute, not a class: `applyComponentStyles` rewrites `className`.
    wrap.toggleAttribute("data-disabled", disabled);
    prev.disabled = disabled;
    value.disabled = disabled;
    next.disabled = disabled;
  };

  const setIndex = (index: number) => {
    if (index < 0) return;
    select.selectedIndex = index;
    select.dispatchEvent(new Event("change", { bubbles: true }));
    sync();
  };

  const cycle = (dir: number) => {
    if (select.disabled) return;
    const current = select.selectedIndex;
    let index = current;
    for (let step = 0; step < select.options.length; step++) {
      index = (index + dir + select.options.length) % select.options.length;
      if (!select.options[index].disabled) {
        setIndex(index);
        return;
      }
    }
  };

  prev.addEventListener("click", () => cycle(-1));
  next.addEventListener("click", () => cycle(1));
  value.addEventListener("click", (e) => cycle(e.shiftKey ? -1 : 1));
  value.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    cycle(-1);
  });

  wrap.addEventListener("keydown", (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      cycle(-1);
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      cycle(1);
    } else if (e.key === "Home") {
      e.preventDefault();
      e.stopPropagation();
      setIndex(firstEnabled(select));
    } else if (e.key === "End") {
      e.preventDefault();
      e.stopPropagation();
      setIndex(lastEnabled(select));
    } else if (e.key === " " || e.key === "Enter") {
      // Keep the game's Space/Enter binds from firing while a cycle control
      // is focused; the button's own activation still runs.
      e.stopPropagation();
    }
  });

  select.addEventListener("change", sync);
  new MutationObserver(sync).observe(select, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["disabled"],
  });

  // Programmatic `select.value = …` (used all over the menu refreshes) must
  // update the visible label too.
  const desc = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, "value");
  if (desc?.get && desc?.set) {
    Object.defineProperty(select, "value", {
      configurable: true,
      enumerable: desc.enumerable,
      get() {
        return desc.get!.call(select);
      },
      set(v: string) {
        desc.set!.call(select, v);
        sync();
      },
    });
  }

  select.after(wrap);
  sync();
}

/** Enhance every select under `root` (defaults to the whole document). */
export function enhanceSelects(root: ParentNode = document): void {
  if (root instanceof HTMLSelectElement) {
    enhanceSelect(root);
    return;
  }
  for (const select of root.querySelectorAll<HTMLSelectElement>("select:not([data-cycle=off])")) {
    enhanceSelect(select);
  }
}

let observer: MutationObserver | null = null;

/**
 * Install the cycle controls and keep watching for dynamically created selects
 * (the sound list, mode editors and every rebuilt profile/preset list create
 * their dropdowns after load).
 */
export function installCycleControls(): void {
  enhanceSelects();
  if (observer) return;
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLSelectElement) enhanceSelect(node);
        else if (node instanceof HTMLElement) enhanceSelects(node);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
